import { ReactNode } from 'react';

import {
  ButtonIcon,
  IconDelete,
  IconEdit,
  IconMore,
  Menu,
  MenuRoot,
  MenuRow,
} from '@spotify-internal/encore-web';
import { Popover } from '@spotify-internal/encore-web/next';
import { Copy, StringUtils } from '@spotify/confidence-core-react';

import styles from './ArtifactMenu.module.css';

interface ItemProps {
  icon: ReactNode;
  children: string;
  onClick?: () => void;
  destructive?: boolean;
  disabled?: boolean;
}

const Item = ({
  icon,
  children,
  onClick,
  destructive,
  disabled,
}: ItemProps) => (
  <MenuRow
    textValue={children}
    onAction={onClick}
    isDisabled={disabled}
    className={destructive ? styles.destructive : undefined}
  >
    {icon}
    {children}
  </MenuRow>
);

const buildRichText = (text: string, href: string): Record<string, string> => ({
  'text/plain': text,
  'text/html': `<a href="${StringUtils.escapeHtml(href)}">${StringUtils.escapeHtml(text)}</a>`,
});

interface RootProps {
  children?: ReactNode;
  'aria-label': string;
  copyUrl?: string;
  displayName?: string;
  onOpen?: () => void;
  onRename?: () => void;
  onDelete?: () => void;
}

const Root = ({
  children,
  'aria-label': ariaLabel,
  copyUrl,
  displayName,
  onOpen,
  onRename,
  onDelete,
}: RootProps) => (
  <MenuRoot
    onOpenChange={isOpen => {
      if (isOpen) onOpen?.();
    }}
  >
    <ButtonIcon
      variant="tertiary"
      size="small"
      aria-label={ariaLabel}
      onClick={e => e.stopPropagation()}
    >
      <IconMore />
    </ButtonIcon>
    <Popover
      hasNoArrow
      placement="bottomEnd"
      onClick={e => e.stopPropagation()}
    >
      <Menu aria-label={ariaLabel}>
        {children}
        {copyUrl && (
          <Copy value={copyUrl} component={Item} label="Copy URL" icon="link" />
        )}
        {displayName && (
          <Copy
            value={copyUrl ? buildRichText(displayName, copyUrl) : displayName}
            component={Item}
            label="Copy name"
            icon="copy"
          />
        )}
        {onRename && (
          <Item icon={<IconEdit size="small" />} onClick={onRename}>
            Rename
          </Item>
        )}
        {onDelete && (
          <Item
            icon={<IconDelete size="small" />}
            onClick={onDelete}
            destructive
          >
            Delete
          </Item>
        )}
      </Menu>
    </Popover>
  </MenuRoot>
);

export const ArtifactMenu = {
  Root,
  Item,
};
